import { action, computed, observable } from "mobx";
import { IInputForms, TerraceInputsConfig } from "./TerraceInputs.labels.config";
import { ParameterController } from 'cd-eft/services/eftPrjAdminApi';
import MessageCenter from "cd-eft/components/common/MessageCenter/MessageCenterVM";
import stores from "cd-eft/models/Stores";
import { HydroControllerApi } from 'cd-eft/services/eftEfh2Api';
import { DesignElementController, TerraceDesignToolsController } from 'cd-eft/services/eftSetApi';
import { Efh2InputParamsDeciSSReq, Efh2OutputParamsDeciSSResSubBasin } from "cd-eft/_codegen_/openapi-generator/eft-efh2";
import { VegetalCoverVM } from "cd-eft/components/EFH2/Waterway Sizing Calulator/VegetalCoversComp/VegetalCoversVM";
import { RunoffSedimentStorageCalReqDTO, RunoffSedimentStorageDTO, SoilParametersDTO, TerraceHydroParamsDTO, AlignedElementDesignParamsRequestDTO } from "cd-eft/_codegen_/openapi-generator/eft-set";
import { formatRound, formatRoundS } from "cd-eft/utils/mathUtilities";
import { getRainfallCellID } from 'cd-eft/services/EFH2/getRainfallCellID';
import { getListOfDesignElements } from 'cd-eft/components/Design/common/ElementContainer/ElementContainerVM';
import Point from '@arcgis/core/geometry/Point';
import { esriProjection, SpatialReference } from 'cd-eft/utils/esriTypes';
import { TerraceUiState } from "cd-eft/models/Stores/TerraceDesignStore";

interface ISelection {
  name: string | undefined;
  value: string | undefined;
}

const designStore = stores.AppStore.designStore;
const terraceDesignStore = stores.AppStore.terraceDesignStore;
const libraryStore = stores.AppStore.projectsLibraryStore;

const defaultStorageSectionInputs = {
  designLife: 10,
  floodRoutingModel: 'PercentageStored',
  percentageStored: 100,
  erosionRate: 5,
  trapEfficiency: 0.9,
  sedimentDensity: 1.2,
  floodDuration: 24
}

const emptyStorageSectionInputs = {
  designLife: '',
  floodRoutingModel: '',
  percentageStored: '',
  erosionRate: '',
  trapEfficiency: '',
  sedimentDensity: '',
  floodDuration: ''
}

const defaultStorageSectionValidation = {
  designLife: true,
  floodRoutingModel: true,
  percentageStored: true,
  erosionRate: true,
  trapEfficiency: true,
  sedimentDensity: true,
  floodDuration: true
}

const defaultStorageSectionRedhighlighting = {
  designLife: false,
  floodRoutingModel: false,
  percentageStored: false,
  erosionRate: false,
  trapEfficiency: false,
  sedimentDensity: false,
  floodDuration: false
}

export class TerraceInputsVM {
  history: any;
  constructor(history) {
    this.history = history;
  }
  @action.bound proceedToTemplate() {
    this.history.push(`/design/${designStore.projectsStore.currentProjectId}/terrace/${terraceDesignStore.terraceDesignObject.alignedElementDesignParametersId}/template`);
  }
  // Local variable terraceInputsConfig could be deleted except for when some dropdown variables' options[] need to be populated from GET APIs in the future.
  @observable terraceInputsConfig = TerraceInputsConfig;

  @observable hasSectionChanged = {
    TerraceInfoSection: false,
    AlignmentSection: false,
    HydrologySection: false,
    SoilStrengthSection: false,
    // VegetalCoverSection: false,
    StorageSection: false
  }
  // free: whenever any input on Terrace Input Page changes, the state is set free
  // otherState: TerraceTemplateSave, TerraceTemplateProceed, ChannelDesignSave ...
  @computed get uiState(): 'free' | 'TerraceInputSave' | 'TerraceInputProceed' | 'otherState' {
    let uiState: 'free' | 'TerraceInputSave' | 'TerraceInputProceed' | 'otherState';
    const globalUIState = terraceDesignStore.uiState;
    if (globalUIState === undefined || this.hasAnyInputChanged) {
      uiState = 'free'
    } else if (globalUIState === 'TerraceInputSave' || globalUIState === 'TerraceInputProceed') {
      uiState = globalUIState
    } else {
      uiState = 'otherState'
    }
    return uiState
  }

  // @action.bound updateUiSate() {
  //   this.localUiSate = 'free';
  // }

  @observable RunoffSedimentStorageCalculationError = false;

  @computed get minimumRequirementForSave() {
    return this.isAllTrue(this.formsValidation.TerraceInfoSection) && this.isAllTrue(this.formsValidation.AlignmentSection)
  }

  @computed get hasAnyInputChanged() {
    return Object.values(this.hasSectionChanged).some(e => e === true) || this.vegetalCoverVM.hasAnyInputChanged;
  };

  @computed get disableSaveAndProceedButton() {
    return (
      !this.isAllTrue(this.formsValidation)
      || this.loading
      || this.vegetalCoverVM.silentCheck
      || this.uiState === 'TerraceInputProceed'
      || this.uiState === 'otherState'
      || this.RunoffSedimentStorageCalculationError
    )
  };

  @computed get disableSaveButton() {
    return (
      this.loading
      || !this.minimumRequirementForSave
      // || !this.hasAnyInputChanged
      || this.hasSectionChanged.HydrologySection && !this.isAllTrue(this.formsValidation.HydrologySection)
      || this.hasSectionChanged.SoilStrengthSection && !this.isAllTrue(this.formsValidation.SoilStrengthSection)
      || this.hasSectionChanged.StorageSection && !this.isAllTrue(this.formsValidation.StorageSection)
      || this.vegetalCoverVM.hasAnyInputChanged && this.vegetalCoverVM.silentCheck
      || this.uiState === 'otherState'
      || this.uiState === 'TerraceInputSave'
      || this.uiState === 'TerraceInputProceed'
    )
  }

  @computed get disableHydrologyCalculateButton() {
    return (
      this.loading
      || !this.isAllTrue(this.formsValidation.TerraceInfoSection)
      || !this.isAllTrue(this.formsValidation.AlignmentSection)
      || !this.isAllTrue(this.formsValidation.HydrologySection)
    )
  }

  @computed get disableRunoffSedimentCalculateButton() {
    return (this.loading || !this.isAllTrue(this.formsValidation) || this.vegetalCoverVM.silentCheck)
  }

  @observable vegetalCoverVM: VegetalCoverVM = new VegetalCoverVM({ mode: 'design' });
  @observable onClickInformation = false;

  @observable hydroResults: Efh2OutputParamsDeciSSResSubBasin | null = null
  @observable sedimentAndRunoffStorageResults: any = null;

  @observable loading: boolean = false;
  @observable isStorageSectionHidden = false;
  @observable elementActions = {
    Edit: { action: () => { } },
    Delete: { action: () => { } },
    Add: { action: () => { } }
  };

  private rainfallCellID: number = 0;
  private latlongInfo: string = '';

  /* need to keep the names of all the keys as the same as TerraceInputsConfig */
  @observable.deep inputForms: IInputForms = {
    TerraceInfoSection: {
      terraceName: '',
      terraceType: '',
      terraceBenchmark: '',
      constructionBenchmark: '',
      benchmarkElevation: 0,
      benchmarkDescription: ''
    },

    AlignmentSection: {
      alignment: '',
      strippingDepth: ''
    },

    HydrologySection: {
      subbasin: 'Hydro_Tool',
      frequency: 10,
      hydrograph: 'Standard-484',
      drainageArea: '',
      curvNumber: '',
      watershedLength: '',
      watershedSlope: '',
    },

    SoilStrengthSection: {
      erodibility: ''
    },

    StorageSection: defaultStorageSectionInputs
  }
  /* need to keep the names of all the keys as the same as TerraceInputsConfig */
  @observable.deep formsValidation = {
    TerraceInfoSection: {
      terraceName: false,
      terraceType: false,
      terraceBenchmark: true, // --UNUSED--
      constructionBenchmark: true, // --UNUSED--
      benchmarkElevation: true, // --UNUSED--
      benchmarkDescription: true // --UNUSED--
    },

    AlignmentSection: {
      alignment: false,
      strippingDepth: true // --UNUSED--
    },

    HydrologySection: {
      // stormRunoff: true,
      subbasin: true,
      frequency: true,
      hydrograph: true, // --UNUSED--
      drainageArea: false,
      curvNumber: false,
      watershedLength: false,
      watershedSlope: false,
    },

    SoilStrengthSection: {
      erodibility: false
    },

    StorageSection: defaultStorageSectionValidation
  }
  @observable.deep redHighlighting = {
    TerraceInfoSection: {
      terraceName: false,
      terraceType: false,
      terraceBenchmark: false, // --UNUSED--
      constructionBenchmark: false, // --UNUSED--
      benchmarkElevation: false, // --UNUSED--
      benchmarkDescription: false // --UNUSED--
    },

    AlignmentSection: {
      alignment: false,
      strippingDepth: false // --UNUSED--
    },

    HydrologySection: {
      // stormRunoff: true,
      subbasin: false,
      frequency: false,
      hydrograph: false, // --UNUSED--
      drainageArea: false,
      curvNumber: false,
      watershedLength: false,
      watershedSlope: false,
    },

    SoilStrengthSection: {
      erodibility: false
    },

    StorageSection: defaultStorageSectionRedhighlighting
  }

  /* need to keep the names of all the keys as the same as subbasinRunoffProps and storageProps*/

  @observable enableCalculationResults = {
    subbasinRunoff: false,
    storage: false,
  }


  @action.bound onClickInformationToggle = () => {
    this.onClickInformation = !this.onClickInformation
  }

  @action.bound  async getAlignmentList() {
    try {
      const response = await DesignElementController.getAllDesignElementsBySessionAndTypeUsingGET(
        designStore.projectsStore.currentSessionId,
        "Alignment"
      );
      const newOptions = response?.data?.map(element => ({
        name: element.designElementName,
        value: element.designElementId,
        optionDisabled: element.flagUpToDate!,
      }));
      this.terraceInputsConfig.AlignmentSection.alignment.options = newOptions;
    } catch (error) {
      console.error("Error fetching alignment options:", error);
    }
  }

  @action.bound async getTerraceInputFormsData() {
    const id = terraceDesignStore.terraceDesignObject.alignedElementDesignParametersId;
    try {
      const responseForBasicInfo = await TerraceDesignToolsController.getTerraceInputsByAlignedElementDesignParamsIdUsingGET(id);
      const responseForHydroInfo = await TerraceDesignToolsController.loadTerraceHydroParamsUsingGET(id);
      const dataForBasicInfo = responseForBasicInfo.data;
      const dataForHydroInfo = responseForHydroInfo.data;

      if (dataForBasicInfo.status === 'Success') {
        const { alignmentJsonId, benchMarkDescription, benchmarkMarkElevation, designElementTypeId, terraceName } = dataForBasicInfo;
        this.inputForms.TerraceInfoSection.terraceName = terraceName!;
        this.inputForms.TerraceInfoSection.terraceType = designElementTypeId!;

        // global parameters
        terraceDesignStore.terraceType = designElementTypeId!;
        terraceDesignStore.alignmentDesignElementJsonId = alignmentJsonId!;

        if (designElementTypeId === 20) {
          this.hideStorageSection();
        } else if (designElementTypeId === 21) {
          this.setDefaultValuesForLevelTerraceType();
        }

        this.inputForms.TerraceInfoSection.benchmarkElevation = benchmarkMarkElevation!;
        this.inputForms.AlignmentSection.alignment = alignmentJsonId!;
        const obj = JSON.parse(benchMarkDescription!);
        this.inputForms.TerraceInfoSection.benchmarkDescription = obj.benchmarkDescription;
        // Construction Benchmark ???
      } else {
        await MessageCenter.addNotificationFromCode(dataForBasicInfo.status!);
      }

      if (dataForHydroInfo.errorResponse) {
        const errorMessage = dataForHydroInfo.errorResponse.errorMessage;
        const errorCode = dataForHydroInfo.errorResponse.errorCode;
        if (errorMessage && dataForHydroInfo.errorResponse.errorType === 'ERROR' && errorCode) {
          MessageCenter.addError(errorMessage, undefined, errorCode);
          this.inputForms.StorageSection = emptyStorageSectionInputs;
        } else if (errorCode) {
          await MessageCenter.addNotificationFromCode(errorCode);
          this.inputForms.StorageSection = emptyStorageSectionInputs;
        }
      } else {

        const basinTypeId = dataForHydroInfo.basinTypeId;
        const basinCharacteristics = dataForHydroInfo.basinCharacteristics;
        const runoffSedimentStorage = dataForHydroInfo.runoffSedimentStorage;
        const soilParameters = dataForHydroInfo.soilParameters;
        const terraceVegetalParameters = dataForHydroInfo.terraceVegetalParameters;

        if (basinCharacteristics) {
          const {
            frequency,
            watershedLength,
            watershedSlope,
            watershedArea,
            watershedCN,
            latLongInfo,
            rainfallCellID,
            peakFlow,
            runoffQ
          } = basinCharacteristics;

          // Hydrology Section result parameters
          this.rainfallCellID = rainfallCellID ? +rainfallCellID! : 0;
          this.latlongInfo = latLongInfo ? latLongInfo : '';

          this.hydroResults = { ...basinCharacteristics, rainfallCellID: +rainfallCellID! };

          this.hydroResults.statusMessage = basinCharacteristics?.status;
          this.enableCalculationResults.subbasinRunoff = true;

          // Subbasin Hydrology Section
          this.inputForms.HydrologySection.frequency = frequency ? frequency : '';
          this.inputForms.HydrologySection.drainageArea = watershedArea ? watershedArea : '';
          this.inputForms.HydrologySection.watershedLength = watershedLength ? watershedLength : '';
          this.inputForms.HydrologySection.watershedSlope = watershedSlope ? watershedSlope : '';
          this.inputForms.HydrologySection.curvNumber = watershedCN ? watershedCN : '';


          // global store 
          terraceDesignStore.hydroInputValues = {
            peakFlow: peakFlow!,
            watershedArea: watershedArea!,
            watershedCN: watershedCN!,
            watershedLength: watershedLength!,
            watershedSlope: watershedSlope!,
            runoffQ: runoffQ!,
          }
        }

        if (runoffSedimentStorage) {
          const {
            designLife,
            percentageStored,
            floodRoutingModel,
            floodDuration,
            erosionRate,
            trapEfficiency,
            sedimentDensity,
            sedimentStorage,
            equivalentRunoffDepthForSediment,
            equivalentRunoffDepthForRunoff,
            requiredDischarge,
            erosionModel,
            runoffStorage
          } = runoffSedimentStorage

          // runoff sediment result 
          if (terraceDesignStore.terraceType !== 20) { // exclude terrace type gradient 

            // intionaly ignore erosionModel in the condiction because it is always NOT null
            if (sedimentStorage || equivalentRunoffDepthForSediment || equivalentRunoffDepthForRunoff || requiredDischarge || runoffStorage) {
              const data = {
                sedimentStorage: sedimentStorage,
                equivalentRunoffDepthForSediment: equivalentRunoffDepthForSediment,
                equivalentRunoffDepthForRunoff: equivalentRunoffDepthForRunoff,
                requiredDischarge: requiredDischarge,
                model: erosionModel,
                runoffStorage: runoffStorage,
              }
              this.updateSedimentAndRunoffStorageResults(data);
            }
          }

          // Sediment & Runoff Storage Section
          this.inputForms.StorageSection.designLife = designLife ? designLife : '';
          this.inputForms.StorageSection.erosionRate = erosionRate ? erosionRate : '';
          this.inputForms.StorageSection.floodDuration = floodDuration ? floodDuration : '';
          this.inputForms.StorageSection.floodRoutingModel = floodRoutingModel ? floodRoutingModel : '';
          this.inputForms.StorageSection.percentageStored = percentageStored ? percentageStored : '';
          this.inputForms.StorageSection.sedimentDensity = sedimentDensity ? sedimentDensity : '';
          this.inputForms.StorageSection.trapEfficiency = trapEfficiency ? trapEfficiency : '';

        }

        // Soil Strength Section 
        if (soilParameters) {
          const stressFactorStr = soilParameters.stressFactorStr;
          this.inputForms.SoilStrengthSection.erodibility = stressFactorStr ? stressFactorStr : '';
        }

        if (terraceVegetalParameters) {
          const {
            vegetalCover, // number 
            vegetalCoverDescription,
            vegetalStabilityOption,
            vegetalCapacityOption,
            vegetalStabilityParams,
            vegetalCapacityParams,
            strippingDepth,
          } = terraceVegetalParameters;

          // Alignment Section
          this.inputForms.AlignmentSection.strippingDepth = strippingDepth ? strippingDepth : '';

          // vegetal cover section
          const vegetalCoverPossibleOption = `${vegetalCoverDescription} (${vegetalCover})`;

          if (this.vegetalCoverVM.vegetalCoverOptions.some(e => e.value === vegetalCoverPossibleOption)) {
            this.vegetalCoverVM.defineVegetalCoverForm.vegetalCoverDropdown = vegetalCoverPossibleOption;
            this.vegetalCoverVM.disableVegetalCoverManualInputs = true;
          } else {
            this.vegetalCoverVM.defineVegetalCoverForm.vegetalCoverDropdown = 'Manual Entry';
            this.vegetalCoverVM.defineVegetalCoverForm.manualboxOne = vegetalCoverDescription!;
            this.vegetalCoverVM.defineVegetalCoverForm.manualboxTwo = vegetalCover!;
            this.vegetalCoverVM.disableVegetalCoverManualInputs = false;
          }

          if (vegetalStabilityOption === 'Retardance Class') {
            this.vegetalCoverVM.RetardanceClassControl = 0;
            this.vegetalCoverVM.defineVegetalCoverForm.retardanceClassForm.retardanceClassForStability = vegetalStabilityParams?.retClassStr!;
            this.vegetalCoverVM.defineVegetalCoverForm.retardanceClassForm.retardanceClassForCapacity = vegetalCapacityParams?.retClassStr!;
          } else if (vegetalStabilityOption === 'Stem length/density') {
            this.vegetalCoverVM.RetardanceClassControl = 2;
            this.vegetalCoverVM.defineVegetalCoverForm.stemLengthDensityForm.stemLengthStabilityLength = vegetalStabilityParams?.stemLength!;
            this.vegetalCoverVM.defineVegetalCoverForm.stemLengthDensityForm.stemLengthStabilityDensity = vegetalStabilityParams?.stemDensity!;
            this.vegetalCoverVM.defineVegetalCoverForm.stemLengthDensityForm.stemLengthCapacityLength = vegetalCapacityParams?.stemLength!;
            this.vegetalCoverVM.defineVegetalCoverForm.stemLengthDensityForm.stemLengthCapacityDensity = vegetalCapacityParams?.stemDensity!;
            await this.vegetalCoverVM.calculateStabilityRci();
            await this.vegetalCoverVM.calculateCapacityRci();
          } else if (vegetalStabilityOption === 'Retardance Curve Index') {
            this.vegetalCoverVM.RetardanceClassControl = 1;
            this.vegetalCoverVM.defineVegetalCoverForm.rciForm.rciCapacity = vegetalCapacityParams?.rci!;
            this.vegetalCoverVM.defineVegetalCoverForm.rciForm.rciStability = vegetalStabilityParams?.rci!;
          } else if (vegetalCapacityOption === "Manning's n") {
            this.vegetalCoverVM.RetardanceClassControl = 3;
            this.vegetalCoverVM.defineVegetalCoverForm.manningForm.manningCapacity = vegetalCapacityParams?.manningsN!;
            this.vegetalCoverVM.defineVegetalCoverForm.manningForm.manningStability = vegetalStabilityParams?.manningsN!;
          }

        }
      }
    } catch (e) {
      console.error(e);
    }
  }

  hideStorageSection() {
    this.isStorageSectionHidden = true;
    this.sedimentAndRunoffStorageResults = null;
    this.enableCalculationResults.storage = false;
    this.inputForms.StorageSection = defaultStorageSectionInputs;
    this.formsValidation.StorageSection = defaultStorageSectionValidation;
    this.redHighlighting.StorageSection = defaultStorageSectionRedhighlighting;
  }

  setDefaultValuesForLevelTerraceType() {
    this.inputForms.StorageSection.floodRoutingModel = 'PercentageStored';
    this.inputForms.StorageSection.percentageStored = 100;
    this.inputForms.StorageSection.floodDuration = 24;

    this.terraceInputsConfig.StorageSection.floodRoutingModel.disabled = true;
    this.terraceInputsConfig.StorageSection.percentageStored.disabled = true;
    this.terraceInputsConfig.StorageSection.floodDuration.disabled = true;

    this.formsValidation.StorageSection.floodRoutingModel = true;
    this.formsValidation.StorageSection.percentageStored = true;
    this.formsValidation.StorageSection.floodDuration = true;

    this.redHighlighting.StorageSection.floodRoutingModel = false;
    this.redHighlighting.StorageSection.percentageStored = false;
    this.redHighlighting.StorageSection.floodDuration = false;
  }

  @action.bound handleInputsOnChange = (e, section) => {
    const { name, value } = e.target;
    const prevValue = this.inputForms[section][name];
    this.inputForms[section][name] = value;
    terraceDesignStore.terraceInputsChanged = true
    if (section === 'TerraceInfoSection' && name === 'terraceType') {
      // Give a confirmation message (OK/CLOSE) if user change terrace type from Storage to Level 
      // (if clicked OK, drain(s) will be removed). Error code : Terrace_34
      if (+prevValue === 22 && +value === 21) {
        const message = MessageCenter.getFromCache('Terrace_34');
        MessageCenter.addWarning(message!, [
          {
            callback: () => {
              this.inputForms[section][name] = prevValue;
              MessageCenter.clearMessages();
            },
            text: 'Cancel',
          },
          {
            callback: async () => {
              const payload = {
                alignedElementDesignParamsId: terraceDesignStore.terraceDesignObject.alignedElementDesignParametersId,
                terraceType: value
              };
              const response = (await TerraceDesignToolsController.deleteReachesBlocksOrDrainsByAlignedElementIdAndTerraceTypeUsingPOST(payload)).data as any;
              if (response) {
                this.onClickSave('save');
                MessageCenter.clearMessages();
              }
            },
            text: 'OK',
          },
        ], 'Terrace_34');
      }

      // Give a confirmation message (OK/Close) if user change terrace type from Storage to Gradient 
      // (if clicked OK, block(s) and drain(s) will be removed). Error code: Terrace_35
      if (+prevValue === 22 && +value === 20) {
        const message = MessageCenter.getFromCache('Terrace_35');
        MessageCenter.addWarning(message!, [
          {
            callback: () => {
              this.inputForms[section][name] = prevValue;
              MessageCenter.clearMessages()
            },
            text: 'Cancel',
          },
          {
            callback: async () => {
              const payload = {
                alignedElementDesignParamsId: terraceDesignStore.terraceDesignObject.alignedElementDesignParametersId,
                terraceType: value
              };
              const response = (await TerraceDesignToolsController.deleteReachesBlocksOrDrainsByAlignedElementIdAndTerraceTypeUsingPOST(payload)).data as any;
              if (response) {
                this.onClickSave('save');
                MessageCenter.clearMessages();
              }
            },
            text: 'OK',
          },
        ], 'Terrace_35');
      }

      // Give a confirmation message (OK/CLOSE) if user change terrace type from Level to Gradient 
      // (if clicked OK, block(s) will be removed). Error code: Terrace_36
      if (+prevValue === 21 && +value === 20) {
        const message = MessageCenter.getFromCache('Terrace_36');
        MessageCenter.addWarning(message!, [
          {
            callback: () => {
              this.inputForms[section][name] = prevValue;
              MessageCenter.clearMessages();
            },
            text: 'Cancel',
          },
          {
            callback: async () => {
              const payload = {
                alignedElementDesignParamsId: terraceDesignStore.terraceDesignObject.alignedElementDesignParametersId,
                terraceType: value
              };
              const response = (await TerraceDesignToolsController.deleteReachesBlocksOrDrainsByAlignedElementIdAndTerraceTypeUsingPOST(payload)).data as any;
              if (response) {
                this.onClickSave('save');
                MessageCenter.clearMessages();
              }
            },
            text: 'OK',
          },
        ], 'Terrace_36');
      }

      // if terraceType is gradient, hide sediment storage section; 
      if (value === '20') {
        this.hideStorageSection();
      }

      else this.isStorageSectionHidden = false;
      // if terraceType is level, make Percentage Stored, Flood Routing Model and Flood Duration read only. 
      if (value === '21') {
        this.setDefaultValuesForLevelTerraceType();
      }
      else {
        this.terraceInputsConfig.StorageSection.floodRoutingModel.disabled = false;
        this.terraceInputsConfig.StorageSection.percentageStored.disabled = false;
        this.terraceInputsConfig.StorageSection.floodDuration.disabled = false;
      }
    }

    if (section === 'StorageSection' && name === 'floodRoutingModel') {
      if (value === "CaldwellModel") {
        this.inputForms.StorageSection.percentageStored = 100;
        this.formsValidation.StorageSection.percentageStored = true;
        this.redHighlighting.StorageSection.percentageStored = false;
        this.terraceInputsConfig.StorageSection.percentageStored.disabled = true;
        this.terraceInputsConfig.StorageSection.percentageStored.hidden = true;
      }
      else {
        this.terraceInputsConfig.StorageSection.percentageStored.disabled = false;
        this.terraceInputsConfig.StorageSection.percentageStored.hidden = false;
      }
    }
  }

  @action.bound handleInputsOnBlur = async (e, section, blockErrorCode = false) => {
    // this.uiState = 'free';
    this.hasSectionChanged[section] = true;
    await terraceDesignStore.handleInputsOnBlur(e, section, blockErrorCode, this.terraceInputsConfig, this.formsValidation, this.redHighlighting, this.handleInputsOnChange, false);
    if (section === "StorageSection" && e.target.name === "floodDuration" && e.target.value > 24) {
      MessageCenter.addNotificationFromCode("Terrace_11");
    } else {
      if (section === 'StorageSection' && e.target.name === 'designLife' && (e.target.value < 1 || e.target.value > 10)) {
        this.redHighlighting.StorageSection.designLife = true;
        ((parseFloat(this.inputForms.StorageSection.designLife.toString()) < 1) ? this.inputForms.StorageSection.designLife = 0 : this.inputForms.StorageSection.designLife = e.target.value);
      }
    }
  }

  @action.bound validateForms = async () => {
    for (const sectionKey of Object.keys(this.inputForms)) {
      const section = this.inputForms[sectionKey];
      for (const key of Object.keys(section)) {
        const e = {
          target: {
            name: key,
            value: section[key],
          },
        };

        await terraceDesignStore.handleInputsOnBlur(e, sectionKey, true, this.terraceInputsConfig, this.formsValidation, this.redHighlighting, this.handleInputsOnChange, true);
      }
    }
  };

  calculateCentroid(points: any): any {
    if (points && points.length > 0) {
      let sumX = 0;
      let sumY = 0;
      let sumZ = 0;
      for (const point of points) {
        sumX += point.x;
        sumY += point.y;
        sumZ += point.z;
      }
      const centroid = new Point({
        spatialReference: libraryStore.projectSpatialReference,
        hasZ: true,
        x: sumX / points.length,
        y: sumY / points.length,
        z: sumY / points.length,
      });

      return centroid;
    } else {
      return undefined;
    }
  }

  @action.bound async getRainfullCellIDAndLatLongInfo(alignmentId: string) {
    const designElementsDataArray: any = await getListOfDesignElements(alignmentId);
    let centroid: any;
    designElementsDataArray.forEach((element: any) => {
      const jsonData = JSON.parse(element.json);
      const alignment = jsonData.alignmentXY;
      centroid = this.calculateCentroid(alignment) as Point;
    });
    const rainfallCellID = await getRainfallCellID(centroid);

    const wgs84 = new SpatialReference({
      wkid: 4326
    });

    const projectedCentroid = esriProjection.project(centroid, wgs84) as Point;
    let latlongInfo = '';
    if (projectedCentroid.latitude && projectedCentroid.longitude) {
      latlongInfo = projectedCentroid.latitude + ', ' + projectedCentroid.longitude;
    }
    this.rainfallCellID = rainfallCellID;
    this.latlongInfo = latlongInfo;
  }
  // helper function 
  // Recursively check nested objects and if any leaf element is not true, return false
  isAllTrue(obj) {
    for (const key in obj) {
      if (typeof obj[key] === 'object') {
        if (!this.isAllTrue(obj[key])) {
          return false;
        }
      } else if (obj[key] !== true) {
        return false;
      }
    }
    return true;
  }

  @action.bound async calculateSubbasinApi() {
    await this.getRainfullCellIDAndLatLongInfo(this.inputForms['AlignmentSection']['alignment'].toString())
    const requestPayload: Efh2InputParamsDeciSSReq = {
      client: designStore.projectsStore.currentProject.client,
      state: designStore.projectsStore.currentProject.state,
      county: designStore.projectsStore.currentProject.county,
      latlonInfo: this.latlongInfo,
      practice: designStore.projectsStore.currentProject.practice,
      user: designStore.projectsStore.currentProject.createdBy,
      createDate: designStore.projectsStore.currentProject.createdDate,
      drainageArea: +this.inputForms.HydrologySection.drainageArea,
      watershedLen: +this.inputForms.HydrologySection.watershedLength,
      timeC: 0.101,
      runoffCN: +this.inputForms.HydrologySection.curvNumber,
      watershedSlope: +this.inputForms.HydrologySection.watershedSlope,
      dimUnitHydrog: this.inputForms.HydrologySection.hydrograph,
      dimUnitHydrogID: 0,
      rainfallCellID: this.rainfallCellID,
      stormFrequency: `${this.inputForms.HydrologySection.frequency} year`,
      rdt_smoothing: "true",
      lulc_h: undefined,
      note: "",
      metadata: "",
      projectUuid: designStore.projectsStore.currentProject.projectUuid,
      demServiceSelected: "",
      demServiceMetadataUrl: ""
    }
    const response = await HydroControllerApi.generateWinTR20ResultsSiteSpecificSubBasinUsingPOST(requestPayload);

    return response;
  }


  @action.bound  async calculateSubbasinResults(asyncFn?) {
    this.loading = true;
    try {
      const response = await this.calculateSubbasinApi();
      if (response && response.data) {
        if (response.data.statusMessage?.toLowerCase() === 'success') {
          this.enableCalculationResults.subbasinRunoff = true;
          this.hydroResults = response.data;
          // this.hasSectionChanged.TerraceInfoSection = false;
          // this.hasSectionChanged.AlignmentSection = false;
          // this.hasSectionChanged.HydrologySection = false;
          if (asyncFn) {
            await asyncFn();
          }

        }
        else {
          this.enableCalculationResults.subbasinRunoff = false;
          MessageCenter.resetMessageCenter();
          await MessageCenter.addNotificationFromCode(response.data.statusMessage!);
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      this.loading = false
    }
  }

  /* ---------------------helper functions START-------------------------*/
  roundingHelperString(value: number, maxDecimals: number) {
    return formatRoundS(value, maxDecimals);
  }
  roundingHelperNumber(value: number, maxDecimals: number) {
    return formatRound(value, maxDecimals);
  }

  turnOffHasAllChangeFlags() {
    Object.keys(this.hasSectionChanged).forEach((key) => {
      this.hasSectionChanged[key] = false;
    });
    this.vegetalCoverVM.turnOffHasInputChangedFlag();
  }
  constructBasinCharacteristicsDTO() {
    if (!this.hydroResults) return '';
    const object = {
      basinName: this.hydroResults.basinName,
      basinBoundary: undefined,
      basinOutlet: undefined,
      dimUnitHydrog: this.hydroResults.dimUnitHydrog,
      frequency: this.inputForms.HydrologySection.frequency,
      isFullHydroModel: this.hydroResults.fullHydroModel === undefined ? false : this.hydroResults.fullHydroModel, // ---
      latLongInfo: this.hydroResults.latLongInfo,
      peakFlow: this.hydroResults.peakFlow,
      precipitation: 4.7,//this.hydroResults.precipitation,// ---HARDCODED---
      qu: this.hydroResults.qu,
      rainfallCellID: this.hydroResults.rainfallCellID?.toString(),
      runoffQ: this.hydroResults.runoffQ,
      status: this.hydroResults.statusMessage,
      stormType: "II",//'MSE3',// ---HARDCODED---
      timeOfConcentrate: this.hydroResults.timeOfConcentrate,
      totalRunOff: this.hydroResults.totalRunOff,
      watershedArea: this.hydroResults.watershedArea,
      watershedCN: this.hydroResults.watershedCN,
      watershedLength: this.hydroResults.watershedLength,
      watershedSlope: this.hydroResults.watershedSlope,
    };

    return object;
  }

  constructRunoffSedimentStorageObject(): RunoffSedimentStorageDTO {
    const { StorageSection } = this.inputForms;
    // use part of response payload RunoffSedimentStorageDTO for format
    const object: RunoffSedimentStorageDTO = {
      designLife: StorageSection.designLife as number,
      floodRoutingModel: StorageSection.floodRoutingModel,
      percentageStored: StorageSection.percentageStored as number,
      erosionRate: StorageSection.erosionRate as number,
      trapEfficiency: StorageSection.trapEfficiency as number,
      sedimentDensity: StorageSection.sedimentDensity as number,
      floodDuration: StorageSection.floodDuration as number,
      erosionModel: 'Simple Erosion'// ---HARDCODED---
    }

    return object
  }

  constructSoilParametersDTO(): SoilParametersDTO {
    const selectedErodibilityValue = this.inputForms.SoilStrengthSection.erodibility;
    const element = this.terraceInputsConfig.SoilStrengthSection.erodibility.options.find(
      item => item.value === selectedErodibilityValue
    );
    if (!element) {
      throw new Error(`Erodibility option not found: ${selectedErodibilityValue}`);
    }
    const { roughness: soilGrainRoughness, allowableStress: allowableStress, value: stressFactorStr } = element;

    // Construct the SoilParametersDTO object, the same as waterway sizing calculator 
    const soilParameters: SoilParametersDTO = {
      soilTypeStr: 'CL',// ---HARDCODED---
      voidRatio: 0,
      plasticityIndex: 0,
      d75: 0,
      allowableStress,
      soilGrainRoughness,
      stressFactorStr,
      inputModeStr: "Soil Stress"// ---HARDCODED--- this one probably doesn't have an effect right now
    };

    return soilParameters;
  }

  constructTerraceVegetalParametersJson() {
    const vegetalCoverWSC = this.vegetalCoverVM.exportVegetalCoverDTO;
    const vegetalCapacityParams_RCI = vegetalCoverWSC.vegetalStabilityOption! === "Manning's n" ? vegetalCoverWSC.vegetalCapacityParams?.manningsN : vegetalCoverWSC.vegetalCapacityParams?.rci;
    delete vegetalCoverWSC.vegetalCapacityParams?.rci;
    vegetalCoverWSC.vegetalCapacityParams = { ...vegetalCoverWSC.vegetalCapacityParams, rci: vegetalCapacityParams_RCI } as any;
    const vegetalStabilityParams_RCI = vegetalCoverWSC.vegetalStabilityOption! === "Manning's n" ? vegetalCoverWSC.vegetalStabilityParams?.manningsN : vegetalCoverWSC.vegetalStabilityParams?.rci;
    delete vegetalCoverWSC.vegetalStabilityParams?.rci;
    vegetalCoverWSC.vegetalStabilityParams = { ...vegetalCoverWSC.vegetalStabilityParams, rci: vegetalStabilityParams_RCI } as any;
    const terraceVegetalParameters = {
      vegetalCover: vegetalCoverWSC.vcffactor,
      vegetalCoverDescription: vegetalCoverWSC.vcDescription,
      vegetalStabilityOption: vegetalCoverWSC.vegetalStabilityOption,
      vegetalCapacityOption: vegetalCoverWSC.vegetalCapacityOption,
      vegetalStabilityParams: vegetalCoverWSC.vegetalStabilityParams,
      vegetalCapacityParams: vegetalCoverWSC.vegetalCapacityParams,
      // strippingDepth: this.inputForms.AlignmentSection.strippingDepth ? this.inputForms.AlignmentSection.strippingDepth : 0
    }
    return terraceVegetalParameters
  }

  // Handle runoff and sediment storage calculation
  async handleRunoffSedimentStorageCalculation() {
    const requestPayload: RunoffSedimentStorageCalReqDTO = this.constructRequestPayload();
    const response = await TerraceDesignToolsController.runoffSedimentStorageCalculationUsingPOST(requestPayload);

    if (response.data) {
      const data: RunoffSedimentStorageDTO = response.data;
      if (data.errorResponse && data.errorResponse.errorType === 'ERROR' && data.errorResponse.errorCode && data.errorResponse.errorMessage) {
        this.enableCalculationResults.storage = false;
        this.RunoffSedimentStorageCalculationError = true;
        MessageCenter.addNotification(data.errorResponse.errorMessage, data.errorResponse.errorType, data.errorResponse.errorCode);
      } else if (!data.errorResponse) {
        this.updateSedimentAndRunoffStorageResults(data);
        this.RunoffSedimentStorageCalculationError = false;
      }
    }
  }

  // Construct the request payload
  constructRequestPayload(): RunoffSedimentStorageCalReqDTO {
    const { TerraceInfoSection, AlignmentSection } = this.inputForms;

    const terraceHydroParams: TerraceHydroParamsDTO = {
      alignedElementDesignParamsId: terraceDesignStore.terraceDesignObject.alignedElementDesignParametersId,
      alignmentDesignElementJsonId: +AlignmentSection.alignment,
      basinCharacteristicsJson: undefined,//JSON.stringify(this.constructBasinCharacteristicsDTO()),// undefined if basingType !== 1 in save HydroParams payload.
      basinName: this.hydroResults?.basinName,
      basinTypeId: 3, // User-defined basin type is 3
      comments: '',
      hydroParamsId: 0,
      userDefinedBasinJson: JSON.stringify(this.constructBasinCharacteristicsDTO()),
      runoffSedimentStorageJson: JSON.stringify(this.constructRunoffSedimentStorageObject()),
      soilParametersJson: JSON.stringify(this.constructSoilParametersDTO()),
      // strippingDepth: AlignmentSection.strippingDepth as number,
      terraceVegetalParametersJson: JSON.stringify(this.constructTerraceVegetalParametersJson()),
    };

    const sedimentAndRunofStorage = {
      terraceName: TerraceInfoSection.terraceName,
      terraceTypeId: +TerraceInfoSection.terraceType,
      terraceHydroParams: terraceHydroParams,

    }

    return sedimentAndRunofStorage

  }

  // Update results with calculated data
  updateSedimentAndRunoffStorageResults(data: RunoffSedimentStorageDTO) {
    this.sedimentAndRunoffStorageResults = {
      sedimentStorage: data.sedimentStorage,
      equivalentRunoffDepthForSediment: data.equivalentRunoffDepthForSediment,
      equivalentRunoffDepthForRunoff: data.equivalentRunoffDepthForRunoff,
      requiredDischarge: data.requiredDischarge,
      model: data.erosionModel ? data.erosionModel : 'Simple Model',// 'Simple Erosion',
      runoffStorage: data.runoffStorage,
    };
    this.enableCalculationResults.storage = true;
  }

  // Reset calculation results, turn off results
  resetCalculationResults() {
    this.enableCalculationResults.storage = false;
    this.sedimentAndRunoffStorageResults = null;
    this.enableCalculationResults.subbasinRunoff = false;
    this.hydroResults = null;
  }
  /* ---------------------------helper functions END--------------------------*/

  @action.bound async calculateSedimenAndRunoffStorageResults() {
    this.loading = true;
    try {
      if (
        this.hydroResults === null
        ||
        this.hasSectionChanged.TerraceInfoSection ||
        this.hasSectionChanged.AlignmentSection ||
        this.hasSectionChanged.HydrologySection
      ) {
        await this.calculateSubbasinResults();
        await this.handleRunoffSedimentStorageCalculation();
      } else {
        await this.handleRunoffSedimentStorageCalculation();
      }
    } catch (error) {
      console.error("Error in calculateSedimenAndRunoffStorageResults:", error);
    } finally {

      this.loading = false
    }
  }

  constructTerraceHydroParamsDto(type: 'save' | 'saveAndProceed'): TerraceHydroParamsDTO {
    const isHydrologyValid = this.isAllTrue(this.formsValidation.HydrologySection);
    const isSoilValid = this.isAllTrue(this.formsValidation.SoilStrengthSection);
    const isVegetalCoverValid = !this.vegetalCoverVM.silentCheck;
    let isRunoffStoragevalid = false;
    if (this.inputForms.TerraceInfoSection.terraceType !== 20) {
      isRunoffStoragevalid = this.isAllTrue(this.formsValidation.StorageSection);
    }

    let inputSaveIndicator;
    if (type === 'save') {
      inputSaveIndicator = 1;
    } else {
      inputSaveIndicator = 2;
    }

    return {
      inputSaveIndicator: inputSaveIndicator,
      alignedElementDesignParamsId: terraceDesignStore.terraceDesignObject.alignedElementDesignParametersId,
      alignmentDesignElementJsonId: +this.inputForms.AlignmentSection.alignment,
      basinCharacteristicsJson: undefined,//JSON.stringify(this.constructBasinCharacteristicsDTO()),// should be excluded if basinTypeId !== 1
      basinName: this.hydroResults?.basinName,
      basinTypeId: 3, // User-defined basin type is 3
      comments: '',
      hydroParamsId: 0,
      userDefinedBasinJson: isHydrologyValid ? JSON.stringify(this.constructBasinCharacteristicsDTO()) : undefined,
      runoffSedimentStorageJson: isRunoffStoragevalid ? JSON.stringify(this.constructRunoffSedimentStorageObject()) : undefined,
      soilParametersJson: isSoilValid ? JSON.stringify(this.constructSoilParametersDTO()) : undefined,
      terraceVegetalParametersJson: isVegetalCoverValid ? JSON.stringify(this.constructTerraceVegetalParametersJson()) : undefined,
      strippingDepth: this.inputForms.AlignmentSection.strippingDepth ? this.inputForms.AlignmentSection.strippingDepth : 0
    }
  }

  //api call to receive all Soil data
  @action.bound async getSoilParameters() {
    let erodibilityMenu: any[] = [];
    let erodibilityOptions: { name: string, value: any }[] = [];
    try {
      const response: any = await ParameterController.getDefaultParameterByIdUsingGET(1);
      if (response.data) {
        const object = JSON.parse(response.data.json);
        const erodibilityObject = object['stressFactors'];
        erodibilityMenu = erodibilityObject.map((e) => ({ label: e['lable'], soilType: e['explanation'], allowableStress: e['stressFactorValue'], roughness: e['roughness'] }));
        erodibilityOptions = erodibilityMenu.map((e) => ({
          name: e['label'].split('_').reduce((prev, current) => (prev + ' ' + current)) + ', ' + 'Allowable Stress (' + e['allowableStress'].toString() + ' lb/sq ft), Soil Type (' + e['soilType'] + ')',
          value: e['label'],
          allowableStress: e.allowableStress,
          roughness: e.roughness
        }));
        this.terraceInputsConfig.SoilStrengthSection.erodibility.options = erodibilityOptions;
      }
    } catch (error) {
      console.error(' error: get waterway sizing calculator getDefaultParameterByIdUsingGET error', error);
    }
  }


  @action.bound async initializeDropdownInputsAndRestoreData() {
    this.loading = true;
    await this.getSoilParameters();
    await this.vegetalCoverVM.getVegetalParameters();
    await this.getAlignmentList();
    // getTerraceInputFormsData() has to be called after the three functions above
    if (terraceDesignStore.terraceDesignObject.alignedElementDesignParametersId > 0) {
      await this.getTerraceInputFormsData();
      // initialize validationForm 
      await this.validateForms();
      await this.vegetalCoverVM.fullSelectiveValidation(true);
      this.vegetalCoverVM.turnOffHasInputChangedFlag();
      terraceDesignStore.terraceInputsChanged = false;
    }

    this.terraceInputsConfig.StorageSection.percentageStored.hidden = this.hidePercentStorage;
    MessageCenter.fillMessageCacheByOriginalCodes(['Terrace_34', 'Terrace_35', 'Terrace_36']);
    this.loading = false;
  }

  @action.bound async onClickSave(type: 'save' | 'saveAndProceed') {
    try {
      this.loading = true;
      const requestPayload: AlignedElementDesignParamsRequestDTO = {
        alignedElementDesignParamsId: terraceDesignStore.terraceDesignObject.alignedElementDesignParametersId,
        alignedFeatureDesignShapeJson: "",
        alignedFeatureName: this.inputForms.TerraceInfoSection.terraceName,
        alignedFeatureSCChange: 0,
        alignmentChange: 0,
        alignmentDesignElementJsonId: +this.inputForms.AlignmentSection.alignment,
        alignmentSeedingBuffer: 0,
        benchmarkElevation: this.inputForms.TerraceInfoSection.benchmarkElevation,
        benchmarkName: this.inputForms.TerraceInfoSection.constructionBenchmark,
        channelEndGradeLength: 0,
        channelStartGradeLength: 0,
        // comments: "",
        constructBenchmark: this.inputForms.TerraceInfoSection.constructionBenchmark,
        designControlJson: "",
        designElementTypeId: +this.inputForms.TerraceInfoSection.terraceType,
        hydroChange: 0,
        hydroParamsJson: "",
        seedingBufferOption: 0,
        sessionUuid: designStore.projectsStore.currentSessionId,
        surfaceChange: 0,
        waterwayMiscDTO: JSON.stringify({
          benchmarkDescription: this.inputForms.TerraceInfoSection.benchmarkDescription,
          spoilDisposal: "",
          waterwayMaterial: ""
        })
      }
      const response = await TerraceDesignToolsController.createOrUpdateTerraceAlignedElementDesignParamsUsingPOST(requestPayload);
      const data = response.data;
      const errorResponse = data.errorResponse;
      if (errorResponse && errorResponse.errorType && errorResponse.errorCode && errorResponse.errorMessage) {
        MessageCenter.addNotification(errorResponse.errorMessage, errorResponse.errorType, errorResponse.errorCode);
        return;
      }
      if (type === 'save' && requestPayload.alignedElementDesignParamsId === 0 && data.alignedElementDesignParamsId) {
        window.history.replaceState({}, '', `/design/${designStore.projectsStore.currentProjectId}/terrace/${data.alignedElementDesignParamsId}/input`)
      }
      terraceDesignStore.terraceDesignObject.alignedElementDesignParametersId = data.alignedElementDesignParamsId!
      if ((!this.hydroResults && this.isAllTrue(this.formsValidation.HydrologySection)) || (this.hydroResults && (this.hasSectionChanged.TerraceInfoSection || this.hasSectionChanged.AlignmentSection || this.hasSectionChanged.HydrologySection) && this.isAllTrue(this.formsValidation.HydrologySection))) {
        const fn = async () => { await this.processAPICreateOrUpdateTerraceHydroParamsByAlignmentId(type) }
        await this.calculateSubbasinResults(fn);
      }
      else if (!this.isAllTrue(this.formsValidation.HydrologySection) || !(this.hasSectionChanged.TerraceInfoSection || this.hasSectionChanged.AlignmentSection || this.hasSectionChanged.HydrologySection) && this.hydroResults) {
        await this.processAPICreateOrUpdateTerraceHydroParamsByAlignmentId(type);
      }
      //update UI state to "TerraceInputSave" on Save
      if (type === 'save') {
        await terraceDesignStore.handleUiStateUpdate({ uiStateType: TerraceUiState.TerraceInputSave.string })
      } else {
        await terraceDesignStore.handleUiStateUpdate({ uiStateType: TerraceUiState.TerraceInputProceed.string })
      }
      terraceDesignStore.summaryInfo.terraceNameExists = true;

      if (type === 'save') {
        // this.uiState = 'TerraceInputSave';
        if (!this.isAllTrue(this.formsValidation) || this.vegetalCoverVM.silentCheck) {
          await MessageCenter.addNotificationFromCode('Terrace_02');
        }
      } else if (type === 'saveAndProceed') {
        // this.uiState = 'TerraceInputProceed';
        this.proceedToTemplate();
      }

    } catch (error) {
      console.error("Error saving terrace data:", error);
    } finally {
      this.loading = false;
    }
  }

  async processAPICreateOrUpdateTerraceHydroParamsByAlignmentId(type: 'save' | 'saveAndProceed') {
    const payload = this.constructTerraceHydroParamsDto(type);
    const responseHydroSave = await TerraceDesignToolsController.createOrUpdateTerraceHydroParamsByAlignedDesignElementIdUsingPOST(payload);
    const dataHydroSave = responseHydroSave.data;
    const errorResponseHydroSave = dataHydroSave.errorResponse;

    if (errorResponseHydroSave && errorResponseHydroSave.errorType && errorResponseHydroSave.errorCode && errorResponseHydroSave.errorMessage) {
      await MessageCenter.addNotificationFromCode('Terrace_02');
      MessageCenter.addNotification(errorResponseHydroSave.errorMessage, errorResponseHydroSave.errorType, errorResponseHydroSave.errorCode);
      return;
    }

    // if (type === 'save') {
    //   this.uiState = 'TerraceInputSave';
    //   if (!this.isAllTrue(this.formsValidation) || this.vegetalCoverVM.silentCheck) {
    //     await MessageCenter.addNotificationFromCode('Terrace_02');
    //   }
    // } else if (type === 'saveAndProceed') {
    //   this.uiState = 'TerraceInputProceed';
    //   this.proceedToTemplate();
    // }
    terraceDesignStore.terraceInputsChanged = false
    this.saveValuesToStore()
    this.turnOffHasAllChangeFlags();
  }

  saveValuesToStore() {
    terraceDesignStore.alignmentDesignElementJsonId = +this.inputForms.AlignmentSection.alignment;
    terraceDesignStore.terraceType = +this.inputForms.TerraceInfoSection.terraceType;
    let peakFlow = 0;
    if (this.hydroResults && this.hydroResults.peakFlow) {
      peakFlow = this.hydroResults.peakFlow
    }
    terraceDesignStore.hydroInputValues = {
      runoffQ: this.hydroResults ? this.hydroResults.runoffQ ? this.hydroResults.runoffQ : 0 : 0,
      peakFlow: peakFlow,
      watershedArea: this.inputForms.HydrologySection.drainageArea ? this.inputForms.HydrologySection.drainageArea : 0,
      watershedCN: this.inputForms.HydrologySection.curvNumber ? this.inputForms.HydrologySection.curvNumber : 0,
      watershedLength: this.inputForms.HydrologySection.watershedLength ? this.inputForms.HydrologySection.watershedLength : 0,
      watershedSlope: this.inputForms.HydrologySection.watershedSlope ? this.inputForms.HydrologySection.watershedSlope : 0,
    }
  }

  get hidePercentStorage() {
    return (this.inputForms.StorageSection.floodRoutingModel === 'CaldwellModel')
  }

}

